import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
    Container, Grid, Box, Skeleton, Alert,
    IconButton, Snackbar, Pagination, Typography, Divider,
    Breadcrumbs, Link
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import InfoIcon from "@mui/icons-material/Info";
import ApartmentIcon from "@mui/icons-material/Apartment";
import HomeIcon from "@mui/icons-material/Home";

import Unauthorized from "../Layout/Unauthorized";
import BuildingCard from "../components/Buildings/BuildingCard";
import { useUserContext } from '../contexts/UserContext';

const Buildings = () => {
    const { token } = useUserContext();
    const navigate = useNavigate();

    const [buildings, setBuildings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [buildingsError, setBuildingsError] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const [currentPage, setCurrentPage] = useState(1);

    const buildingsPerPage = 8;
    const totalPages = Math.ceil(buildings.length / buildingsPerPage);
    const paginatedBuildings = buildings.slice(
        (currentPage - 1) * buildingsPerPage,
        currentPage * buildingsPerPage
    );

    useEffect(() => {
        if (token) {
            fetchBuildings();
        }
    }, [token]);

    const fetchBuildings = () => {
        axios.get("/buildings/")
            .then(response => {
                setBuildings(response.data);
                setTimeout(() => setLoading(false), 1500);
            })
            .catch(() => {
                setBuildingsError(true);
                setLoading(false);
            });
    };

    const handleDelete = (id) => {
        axios.delete(`/buildings/${id}/`)
            .then(() => {
                fetchBuildings(); // Re-fetch the latest list
                setSnackbar({
                    open: true,
                    message: "Building deleted successfully",
                    severity: "success",
                });
            })
            .catch(() => {
                setSnackbar({
                    open: true,
                    message: "Failed to delete building",
                    severity: "error",
                });
            });
    };

    const renderSkeletonCard = () => (
        <Grid item xs={12} md={6} key={Math.random()}>
            <Box sx={{ height: '100%' }}>
                <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 4, mb: 2 }} />
            </Box>
        </Grid>
    );

    if (!token) {
        return <Unauthorized />;
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '90vh' }}>
            <Container maxWidth="xl" sx={{ my: 5 }}>
                {/* Breadcrumbs */}
                <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
                    <Link
                        underline="hover"
                        color="inherit"
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            navigate('/');
                        }}
                        sx={{ display: 'flex', alignItems: 'center' }}
                    >
                        <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                        Home
                    </Link>
                    <Typography color="text.primary">Buildings</Typography>
                </Breadcrumbs>

                {/* Minimal Page Title */}
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', mb: 2, mt: 1 }}>
                    <ApartmentIcon sx={{ fontSize: 36, color: 'primary.main', mr: 1 }} />
                    <Typography variant="h4" align="center">
                        Buildings
                    </Typography>
                </Box>
                <Divider sx={{ mb: 3 }} />
                {buildingsError ? (
                    <Alert severity="error">No buildings could be loaded. Please try again later.</Alert>
                ) : (
                    <>
                        <Grid container spacing={3}>
                            {loading
                                ? Array.from({ length: 4 }).map(() => renderSkeletonCard())
                                : paginatedBuildings.map(building => (
                                    <Grid item xs={12} md={6} key={building.id}>
                                        <BuildingCard
                                            building={building}
                                            onDelete={() => handleDelete(building.id)}
                                            onDetails={() => navigate(`/buildings/${building.id}`)}
                                        />
                                    </Grid>
                                ))}
                        </Grid>

                        {!loading && totalPages > 1 && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                                <Pagination
                                    count={totalPages}
                                    page={currentPage}
                                    onChange={(e, value) => setCurrentPage(value)}
                                    color="primary"
                                />
                            </Box>
                        )}
                    </>
                )}
            </Container>

            <Box sx={{ textAlign: 'right', pr: 4, mt: 'auto', py: 5 }}>
                <IconButton
                    color="primary"
                    sx={{
                        bgcolor: 'primary.main',
                        color: 'white',
                        width: 70,
                        height: 70,
                        boxShadow: 3,
                        '&:hover': { bgcolor: 'primary.dark' },
                    }}
                    onClick={() => navigate("/add-building")}
                >
                    <AddIcon sx={{ fontSize: 40 }} />
                </IconButton>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mt: 1 }}>
                    <InfoIcon color="primary" sx={{ fontSize: 20, mr: 1 }} />
                    <span style={{ color: '#666', fontSize: 14 }}>
                        Add a building and create its building envelope
                    </span>
                </Box>
            </Box>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <Alert
                    variant="filled"
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: '100%', mb: 5 }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default Buildings;
